import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import '../../backlog/backlog.css';
import '../suboutRegistration/suboutRegistration.css';
import { AppDataGrid, type AppColumn } from '../../../components/AppDataGrid';
import { useNotify } from '../../../components/notifications';
import { useConfirm, useSaveFlow } from '../../../components/confirm';
import { useHotkeys } from '../../../components/hotkeys';
import { UsDateInput } from '../../../components/UsDateInput';
import { useUnsavedGuard } from '../../../hooks/useUnsavedGuard';
import { fmtLocalDate, fmtLocalDateTime } from '../../../utils/datetime';
import { apiMessage } from '../suboutRegistration/api';
import PreLoginDocsPanel from '../../settings/preLoginSettings/PreLoginDocsPanel';
import { ProjectNotesButton } from '../../../components/ProjectNotes';
import { suboutDocsOwner } from '../suboutDocs';
import {
  fetchSuboutResultsOrder, importSuboutEdd, reviewSuboutResults, rollbackSuboutResults,
  saveSuboutResultEntry, submitSuboutResults,
} from './api';
import type {
  SuboutResultEdit, SuboutResultRow, SuboutResultsHeader, SuboutResultsLookups, SuboutResultsMode,
} from './types';

// ─────────────────────────────────────────────────────────────────────────────
// Subcontract Result Entry / Result Validation — the order's full-page detail view
// (G5 SubOutSampleRegistrations_DetailView_Copy_SuboutResultEntry and
// _DetailView_Level2DataReview). The list is REPLACED by this page (standing rule 12).
//
//   entry      — Sample / QC results editable on Pending Entry rows; Save · Save and
//                Close · Cancel; EDD Import; Submit (selected rows).
//   validation — rows awaiting validation, editable; Review (saves edits + reviews the
//                selected rows) and Rollback (reason).
//   history    — every row of the order, read-only; Rollback.
//
// Every rule runs in the API (SuboutResultRules); messages are G5's. The Attachments and
// Notes tabs (entry only, as in G5) are the shared documents panel over the order's rows.
// ─────────────────────────────────────────────────────────────────────────────

const ENTRY_DOCS = suboutDocsOwner('entry');

interface Props {
  orderId: string;
  mode: SuboutResultsMode;
  lookups: SuboutResultsLookups | null;
  canWrite: boolean;
  /** The ids of the list's rows, for the prev / next arrows. */
  siblings: string[];
  onNavigate: (id: string) => void;
  onClose: () => void;
  /** After a write that changes which list the order belongs on. */
  onChanged: () => void;
}

type Tab = 'samples' | 'qc' | 'general' | 'attachments' | 'notes';

/** A row in the editor: the API row + the local datetime-input text of Analyzed Date. */
type DraftRow = SuboutResultRow & { analyzedLocal: string };

const toLocalInput = (iso: string | null): string => (iso ? iso.slice(0, 16) : '');

function toDraft(rows: SuboutResultRow[]): DraftRow[] {
  return rows.map((r) => ({ ...r, analyzedLocal: toLocalInput(r.analyzedDate) }));
}

function toEdit(r: DraftRow): SuboutResultEdit {
  return {
    oid: r.oid, result: r.result, unitsOid: r.unitsOid, df: r.df, loq: r.loq, uql: r.uql, mdl: r.mdl,
    rptLimit: r.rptLimit, analyzedBy: r.analyzedBy, analyzedDate: r.analyzedLocal || null,
    spikeAmount: r.spikeAmount, recovery: r.recovery, rpd: r.rpd, recLCLimit: r.recLCLimit,
    recUCLimit: r.recUCLimit, rpdlcLimit: r.rpdlcLimit, rpducLimit: r.rpducLimit, surrogate: r.surrogate,
  };
}

const STATUS_TEXT: Record<number, string> = {
  0: 'Pending Entry', 3: 'Pending Reporting', 10: 'Reported',
  11: 'Subout Pending Validation', 12: 'Subout Pending Approval',
};

export default function SuboutResultsDetail({
  orderId, mode, lookups, canWrite, siblings, onNavigate, onClose, onChanged,
}: Props) {
  const notify = useNotify();
  const { confirm, prompt, alert } = useConfirm();
  const runSave = useSaveFlow();
  const fileRef = useRef<HTMLInputElement>(null);

  const [header, setHeader]   = useState<SuboutResultsHeader | null>(null);
  const [samples, setSamples] = useState<DraftRow[]>([]);
  const [qc, setQc]           = useState<DraftRow[]>([]);
  const [dirty, setDirty]     = useState<Set<string>>(new Set());
  const [selSamples, setSelSamples] = useState<Set<string | number>>(new Set());
  const [selQc, setSelQc]     = useState<Set<string | number>>(new Set());
  const [loading, setLoading] = useState(true);
  const [busy, setBusy]       = useState(false);
  const [tab, setTab]         = useState<Tab>('samples');
  const [docCounts, setDocCounts] = useState({ attachments: 0, notes: 0 });

  // Header fields editable on Result Entry (G5 General Information).
  const [receivedByOid, setReceivedByOid] = useState('');
  const [dateReceived, setDateReceived]   = useState('');
  const [dateShipped, setDateShipped]     = useState('');
  const [daysElapsed, setDaysElapsed]     = useState('');
  const [remark, setRemark]               = useState('');
  const [headerDirty, setHeaderDirty]     = useState(false);

  const isEntry = mode === 'entry';
  const editableStatus = isEntry ? 0 : mode === 'validation' ? 11 : -1;
  const orderOpen = isEntry ? header?.suboutStatus === 1 : true;
  const canEditRows = canWrite && orderOpen && editableStatus >= 0;
  const isDirty = dirty.size > 0 || headerDirty;
  useUnsavedGuard(isDirty);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const o = await fetchSuboutResultsOrder(orderId, mode);
      setHeader(o.header);
      setSamples(toDraft(o.samples));
      setQc(toDraft(o.qcSamples));
      setDirty(new Set()); setSelSamples(new Set()); setSelQc(new Set());
      setReceivedByOid(o.header.receivedByOid ?? '');
      setDateReceived(o.header.dateReceived ? o.header.dateReceived.slice(0, 10) : '');
      setDateShipped(o.header.dateShipped ? o.header.dateShipped.slice(0, 10) : '');
      setDaysElapsed(o.header.daysElapsed == null ? '' : String(o.header.daysElapsed));
      setRemark(o.header.remark ?? '');
      setHeaderDirty(false);
    } catch (e) {
      notify.error(apiMessage(e, 'Failed to open the subcontract order.'));
    } finally {
      setLoading(false);
    }
  }, [orderId, mode, notify]);

  useEffect(() => { void load(); }, [load]);

  // ── Grid editing ───────────────────────────────────────────────────────────
  const unitNames = useMemo(() => (lookups?.units ?? []).map((u) => u.name ?? '').filter(Boolean), [lookups]);
  const unitIdByName = useMemo(() => {
    const m = new Map<string, string>();
    for (const u of lookups?.units ?? []) if (u.name) m.set(u.name.toLowerCase(), u.id);
    return m;
  }, [lookups]);

  const commit = useCallback((which: 'samples' | 'qc') => (row: DraftRow, col: AppColumn<DraftRow>, value: string) => {
    const apply = (rows: DraftRow[]) => rows.map((r) => {
      if (r.oid !== row.oid) return r;
      if (col.field === 'units') return { ...r, units: value || null, unitsOid: value ? unitIdByName.get(value.toLowerCase()) ?? null : null };
      if (col.field === 'analyzedDate') return { ...r, analyzedLocal: value };
      return { ...r, [col.field]: value || null };
    });
    if (which === 'samples') setSamples(apply); else setQc(apply);
    setDirty((d) => new Set(d).add(row.oid));
  }, [unitIdByName]);

  const locked = useCallback((r: DraftRow) => !canEditRows || r.status !== editableStatus, [canEditRows, editableStatus]);
  const txt = (field: keyof DraftRow, header: string, width = 90): AppColumn<DraftRow> => ({
    field: field as string, header, width,
    text: (r) => (r[field] as string | null) ?? '',
    edit: { type: 'text', disabled: locked },
  });

  const commonResultCols: AppColumn<DraftRow>[] = [
    { ...txt('result', 'Result', 100), edit: { type: 'text', disabled: locked, requiredEmpty: (r) => !locked(r) && !r.result } },
    { field: 'units', header: 'Units', width: 110, text: (r) => r.units ?? '',
      edit: { type: 'combo', options: unitNames, disabled: locked, clearable: true } },
    txt('df', 'DF', 70), txt('loq', 'LOQ', 80), txt('uql', 'UQL', 80), txt('mdl', 'MDL', 80), txt('rptLimit', 'RptLimit', 90),
  ];
  const tailCols: AppColumn<DraftRow>[] = [
    { ...txt('analyzedBy', 'AnalyzedBy', 130), edit: { type: 'text', disabled: locked, requiredEmpty: (r) => !locked(r) && !r.analyzedBy } },
    { field: 'analyzedDate', header: 'AnalyzedDate', width: 150, type: 'datetime',
      value: (r) => r.analyzedLocal, text: (r) => fmtLocalDateTime(r.analyzedLocal || null),
      edit: { type: 'datetime', disabled: locked, display: (v) => fmtLocalDateTime(v || null),
              requiredEmpty: (r) => !locked(r) && !r.analyzedLocal } },
    { field: 'enteredBy', header: 'EnteredBy', width: 110, text: (r) => r.enteredBy ?? '' },
    { field: 'enteredDate', header: 'EnteredDate', width: 140, type: 'datetime', text: (r) => fmtLocalDateTime(r.enteredDate) },
    ...(isEntry ? [] : [
      { field: 'validatedBy', header: 'ValidatedBy', width: 110, text: (r: DraftRow) => r.validatedBy ?? '' },
      { field: 'validatedDate', header: 'ValidatedDate', width: 140, type: 'datetime' as const, text: (r: DraftRow) => fmtLocalDateTime(r.validatedDate) },
    ]),
    { field: 'status', header: 'Status', width: 170, type: 'status', text: (r) => STATUS_TEXT[r.status] ?? String(r.status) },
  ];

  const sampleCols: AppColumn<DraftRow>[] = [
    { field: 'jobId', header: 'JobID', width: 100, text: (r) => r.jobId ?? '' },
    { field: 'sampleId', header: 'SampleID', width: 130, text: (r) => r.sampleId ?? '' },
    { field: 'sampleName', header: 'SampleName', width: 140, text: (r) => r.sampleName ?? '' },
    { field: 'matrix', header: 'Matrix', width: 90, text: (r) => r.matrix ?? '' },
    { field: 'test', header: 'Test', width: 130, text: (r) => r.test ?? '' },
    { field: 'method', header: 'Method', width: 100, text: (r) => r.method ?? '' },
    { field: 'parameter', header: 'Parameter', width: 150, text: (r) => r.parameter ?? '' },
    { field: 'component', header: 'Component', width: 90, text: (r) => r.component ?? '' },
    ...commonResultCols, ...tailCols,
  ];
  const qcCols: AppColumn<DraftRow>[] = [
    { field: 'jobId', header: 'JobID', width: 100, text: (r) => r.jobId ?? '' },
    { field: 'qcType', header: 'QCType', width: 90, text: (r) => r.qcType ?? '' },
    { field: 'sampleId', header: 'SampleID', width: 130, text: (r) => r.sampleId ?? '' },
    { field: 'matrix', header: 'Matrix', width: 90, text: (r) => r.matrix ?? '' },
    { field: 'test', header: 'Test', width: 130, text: (r) => r.test ?? '' },
    { field: 'method', header: 'Method', width: 100, text: (r) => r.method ?? '' },
    { field: 'parameter', header: 'Parameter', width: 150, text: (r) => r.parameter ?? '' },
    ...commonResultCols,
    txt('spikeAmount', 'SpikeAmount', 100), txt('recovery', '%Rec', 70), txt('rpd', '%RPD', 70),
    txt('recLCLimit', 'RecLCLimit', 90), txt('rpdlcLimit', 'RPDLCLimit', 90), txt('rpducLimit', 'RPDUCLimit', 90),
    txt('recUCLimit', 'RecUCLimit', 90), txt('surrogate', 'Surrogate', 90),
    ...tailCols,
  ];

  const editsOf = (rows: DraftRow[]) => rows.filter((r) => dirty.has(r.oid)).map(toEdit);
  const selectedOids = (set: Set<string | number>) => [...set].map(String);

  // ── Result Entry: Save ─────────────────────────────────────────────────────
  async function save(close: boolean) {
    if (!header || busy) return;
    if (!canWrite) { notify.warning('You do not have permission to enter subcontract results.'); return; }
    if (!isDirty) { notify.info('There are no changes to save.'); if (close) onClose(); return; }
    await runSave({
      formName: 'Subout Sample Result Entry',
      auditAction: 'edit',
      save: async () => {
        // The date boxes are date-only but the stored values carry a time (DateReceived is
        // stamped with the receipt time). An untouched date sends the stored value back, so
        // a Save never quietly moves 10:50 to midnight.
        const keep = (edited: string, stored: string | null) =>
          (stored && edited === stored.slice(0, 10)) ? stored : (edited || null);
        await saveSuboutResultEntry(orderId, {
          samples: editsOf(samples), qcSamples: editsOf(qc),
          receivedByOid: receivedByOid || null,
          dateReceived: keep(dateReceived, header.dateReceived),
          dateShipped: keep(dateShipped, header.dateShipped),
          daysElapsed: daysElapsed.trim() === '' ? null : Number(daysElapsed),
          remark: remark.trim() || null,
          lockVersion: header.lockVersion,
        });
      },
      onSaved: () => { if (close) { onChanged(); onClose(); } else void load(); },
    });
  }

  // ── Result Entry: Submit ───────────────────────────────────────────────────
  async function submit() {
    if (!header || busy) return;
    if (!canWrite) { notify.warning('You do not have permission to submit subcontract results.'); return; }
    if (isDirty) { notify.warning('Save your changes before you submit.'); return; }
    const s = selectedOids(selSamples), q = selectedOids(selQc);
    if (s.length + q.length === 0) { notify.warning('Please select an item to proceed further.'); return; }
    if (!await confirm({ title: 'Submit', message: `Submit ${s.length + q.length} selected result${s.length + q.length === 1 ? '' : 's'}?` })) return;
    setBusy(true);
    try {
      const r = await submitSuboutResults(orderId, s, q);
      await alert({ title: 'Success', tone: 'success', message: 'Submitted successfully.' });
      if (r.orderCompleted) { onChanged(); onClose(); } else await load();
    } catch (e) {
      notify.error(apiMessage(e, 'The submit failed.'));
    } finally { setBusy(false); }
  }

  // ── Result Entry: EDD Import ───────────────────────────────────────────────
  async function importFile(file: File) {
    if (!header) return;
    if (isDirty) { notify.warning('Save your changes before you import.'); return; }
    if (!await confirm({ title: 'EDD Import', message: `Import results from ${file.name}?` })) return;
    setBusy(true);
    try {
      const r = await importSuboutEdd(orderId, file);
      const lines = [
        `${r.samplesSaved} sample result${r.samplesSaved === 1 ? '' : 's'} imported from ${r.fileRows} file row${r.fileRows === 1 ? '' : 's'}.`,
        ...(r.qcCreated + r.qcUpdated > 0 ? [`QC: ${r.qcCreated} created, ${r.qcUpdated} updated.`] : []),
        ...r.warnings.slice(0, 12),
        ...(r.warnings.length > 12 ? [`… and ${r.warnings.length - 12} more.`] : []),
      ];
      await alert({ title: r.warnings.length ? 'Imported with warnings' : 'Success',
                    tone: r.warnings.length ? 'warning' : 'success', message: lines.join('\n') });
      await load();
    } catch (e) {
      notify.error(apiMessage(e, 'The import failed.'));
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  }

  // ── Result Validation: Review / Rollback ───────────────────────────────────
  async function review() {
    if (!header || busy) return;
    if (!canWrite) { notify.warning('You do not have permission to review subcontract results.'); return; }
    const s = selectedOids(selSamples), q = selectedOids(selQc);
    if (s.length + q.length === 0) { notify.warning('Please select an item to proceed further.'); return; }
    if (!await confirm({ title: 'Review', message: `Review ${s.length + q.length} selected result${s.length + q.length === 1 ? '' : 's'}?` })) return;
    setBusy(true);
    try {
      const r = await reviewSuboutResults(orderId, { samples: editsOf(samples), qcSamples: editsOf(qc), sampleOids: s, qcOids: q });
      await alert({ title: 'Success', tone: 'success', message: 'Result reviewed successfully.' });
      if (r.orderCompleted) { onChanged(); onClose(); } else await load();
    } catch (e) {
      notify.error(apiMessage(e, 'The review failed.'));
    } finally { setBusy(false); }
  }

  async function rollback() {
    if (!header || busy) return;
    if (!canWrite) { notify.warning('You do not have permission to roll back subcontract results.'); return; }
    if (isDirty) { notify.warning('Save or discard your changes before you roll back.'); return; }
    const s = selectedOids(selSamples), q = selectedOids(selQc);
    if (s.length + q.length === 0) { notify.warning('Please select an item to proceed further.'); return; }
    const reason = await prompt({ title: 'Rollback', message: 'Rollback reason', required: true, confirmLabel: 'Rollback' });
    if (reason == null) return;
    if (!reason.trim()) { notify.warning('Please enter a valid reason.'); return; }
    setBusy(true);
    try {
      await rollbackSuboutResults(orderId, s, q, reason.trim());
      await alert({ title: 'Success', tone: 'success', message: 'Rollbacked successfully.' });
      onChanged(); onClose();
    } catch (e) {
      notify.error(apiMessage(e, 'The rollback failed.'));
    } finally { setBusy(false); }
  }

  async function guardedClose() {
    if (isDirty && !await confirm({ title: 'Cancel', message: 'Unsaved changes will be lost. Do you want to proceed?' })) return;
    onClose();
  }

  // ── Prev / next record ─────────────────────────────────────────────────────
  const pos = siblings.indexOf(orderId);
  const go = async (delta: number) => {
    const next = siblings[pos + delta];
    if (!next) return;
    if (isDirty && !await confirm({ title: 'Navigate', message: 'Unsaved changes will be lost. Do you want to proceed?' })) return;
    onNavigate(next);
  };

  const title = mode === 'entry' ? 'Subcontract Result Entry' : 'Subcontract Result Validation';
  const busyOrLoading = busy || loading;

  useHotkeys([
    ...(isEntry ? [
      { combo: 'mod+s', label: 'Save', group: title, order: 1, disabled: busyOrLoading || !canEditRows, run: () => void save(false) },
      { combo: 'mod+enter', label: 'Save and Close', group: title, order: 2, disabled: busyOrLoading || !canEditRows, run: () => void save(true) },
      { combo: 'alt+u', label: 'Submit', group: title, order: 3, disabled: busyOrLoading || !canEditRows, run: () => void submit() },
      { combo: 'alt+i', label: 'EDD Import', group: title, order: 4, disabled: busyOrLoading || !canEditRows, run: () => fileRef.current?.click() },
    ] : []),
    ...(mode === 'validation' ? [
      { combo: 'alt+v', label: 'Review', group: title, order: 1, disabled: busyOrLoading || !canWrite, run: () => void review() },
    ] : []),
    ...(!isEntry ? [
      { combo: 'alt+b', label: 'Rollback', group: title, order: 2, disabled: busyOrLoading || !canWrite, run: () => void rollback() },
    ] : []),
    { combo: 'alt+r', label: 'Refresh', group: title, order: 5, disabled: busyOrLoading, run: () => void load() },
    { combo: 'alt+left', label: 'Previous record', group: title, order: 6, disabled: pos <= 0, run: () => void go(-1) },
    { combo: 'alt+right', label: 'Next record', group: title, order: 7, disabled: pos < 0 || pos >= siblings.length - 1, run: () => void go(1) },
    { combo: 'esc', label: isEntry ? 'Cancel' : 'Close', group: title, order: 9, allowInInput: false, disabled: busy, run: () => void guardedClose() },
  ]);

  const actionBar = (
    <div className="srd-actions">
      <div className="srd-actions-left">
        {isEntry && (
          <>
            <button type="button" className="ca-btn" disabled={busyOrLoading || !canEditRows}
                    title="EDD Import (Alt+I)" onClick={() => fileRef.current?.click()}>
              <i className="ti ti-file-import" aria-hidden="true" /> EDD Import
            </button>
            <button type="button" className="ca-btn" disabled={busyOrLoading || !canEditRows}
                    title="Submit (Alt+U)" onClick={() => void submit()}>
              <i className="ti ti-send" aria-hidden="true" /> Submit
            </button>
          </>
        )}
        {mode === 'validation' && (
          <button type="button" className="ca-btn" disabled={busyOrLoading || !canWrite}
                  title="Review (Alt+V)" onClick={() => void review()}>
            <i className="ti ti-checks" aria-hidden="true" /> Review
          </button>
        )}
        {!isEntry && (
          <button type="button" className="ca-btn" disabled={busyOrLoading || !canWrite}
                  title="Rollback (Alt+B)" onClick={() => void rollback()}>
            <i className="ti ti-arrow-back-up" aria-hidden="true" /> Rollback
          </button>
        )}
        <button type="button" className="ca-btn" disabled={busyOrLoading} title="Refresh (Alt+R)" onClick={() => void load()}>
          <i className="ti ti-refresh" aria-hidden="true" /> Refresh
        </button>
        {/* G5 Project Notes: the order's own notes plus its jobs' (shared dbo.Notes). */}
        <ProjectNotesButton
          recordId={orderId}
          baseUrl={`/api/v1/subout-orders/${isEntry ? 'entry' : 'validation'}/${orderId}/project-notes`}
          recordLabel={header?.suboutOrderId ?? ''}
          businessObject={title}
          readOnly={!canWrite}
        />
      </div>
      <div className="srd-actions-right">
        {isEntry && (
          <>
            <button type="button" className="ca-btn ca-btn-solid" disabled={busyOrLoading || !canEditRows}
                    title="Save (Ctrl+S)" onClick={() => void save(false)}>
              <i className="ti ti-device-floppy" aria-hidden="true" /> Save
            </button>
            <button type="button" className="ca-btn ca-btn-solid" disabled={busyOrLoading || !canEditRows}
                    title="Save and Close (Ctrl+Enter)" onClick={() => void save(true)}>
              <i className="ti ti-check" aria-hidden="true" /> Save and Close
            </button>
          </>
        )}
        <button type="button" className="ca-btn" disabled={busy} title={`${isEntry ? 'Cancel' : 'Close'} (Esc)`}
                onClick={() => void guardedClose()}>
          <i className="ti ti-x" aria-hidden="true" /> {isEntry ? 'Cancel' : 'Close'}
        </button>
      </div>
    </div>
  );

  const orderLabel = header ? `${header.suboutOrderId ?? ''}_${header.contractLab ?? ''}` : '…';
  const tabs: { key: Tab; label: string }[] = [
    { key: 'samples', label: `Sample Results (${samples.length})` },
    { key: 'qc', label: `QC Sample Results (${qc.length})` },
    { key: 'general', label: 'General Information' },
    ...(isEntry ? [
      { key: 'attachments' as Tab, label: `Attachments (${docCounts.attachments})` },
      { key: 'notes' as Tab, label: `Notes (${docCounts.notes})` },
    ] : []),
  ];
  const headerReadOnly = !isEntry || !canEditRows;
  const markHeader = <T,>(setter: (v: T) => void) => (v: T) => { setter(v); setHeaderDirty(true); };

  return (
    <div className="bl-page srd-page" data-unsaved-ignore>
      <input ref={fileRef} type="file" accept=".xlsx,.xls,.csv" hidden
             onChange={(e) => { const f = e.target.files?.[0]; if (f) void importFile(f); }} />

      <div className="srd-head">
        <div className="dp-breadcrumb">
          <span>Sample SubContract Tracking</span><i className="ti ti-chevron-right" />
          <span>{title}{mode === 'history' ? ' · History' : ''}</span><i className="ti ti-chevron-right" />
          <span className="dp-bc-active">{header?.suboutOrderId ?? '…'}</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <h2 className="srd-title" style={{ flex: 1 }}>{orderLabel}</h2>
          <button type="button" className="ca-btn" aria-label="Previous record" title="Previous record (Alt+←)"
                  disabled={pos <= 0} onClick={() => void go(-1)}><i className="ti ti-chevron-left" /></button>
          <button type="button" className="ca-btn" aria-label="Next record" title="Next record (Alt+→)"
                  disabled={pos < 0 || pos >= siblings.length - 1} onClick={() => void go(1)}><i className="ti ti-chevron-right" /></button>
        </div>
        {isEntry && header && header.suboutStatus !== 1 && (
          <div className="srd-tab-stub" role="status">This order is no longer pending result entry — it is read-only.</div>
        )}
        {header?.rollBackReason && (
          <div className="srd-tab-stub" role="status">Rolled back: {header.rollBackReason}</div>
        )}
      </div>

      {actionBar}

      <div className="srd-body">
        <div className="srd-tabs" role="tablist">
          {tabs.map((t) => (
            <button key={t.key} type="button" role="tab" aria-selected={tab === t.key}
                    className={tab === t.key ? 'on' : ''} onClick={() => setTab(t.key)}>{t.label}</button>
          ))}
        </div>

        {tab === 'samples' && (
          <div className="srd-tabpane">
            <AppDataGrid<DraftRow>
              rows={samples} columns={sampleCols} getRowId={(r) => r.oid} loading={loading}
              selectable selectedIds={selSamples} onSelectionChange={setSelSamples}
              onCellCommit={commit('samples')}
              maximizable exportFilename={`subcontract-sample-results-${header?.suboutOrderId ?? ''}`}
              emptyText={isEntry ? 'No sample results for this order.' : 'No sample results awaiting validation.'}
            />
          </div>
        )}
        {tab === 'qc' && (
          <div className="srd-tabpane">
            <AppDataGrid<DraftRow>
              rows={qc} columns={qcCols} getRowId={(r) => r.oid} loading={loading}
              selectable selectedIds={selQc} onSelectionChange={setSelQc}
              onCellCommit={commit('qc')}
              maximizable exportFilename={`subcontract-qc-results-${header?.suboutOrderId ?? ''}`}
              emptyText="No QC sample results."
            />
          </div>
        )}
        {tab === 'general' && header && (
          <section className="srd-sec">
            <h3>General Information</h3>
            <div className="srd-grid">
              <label>SuboutOrderID</label>
              <input value={header.suboutOrderId ?? ''} readOnly className="srd-ro" />
              <label>DeliveryService</label>
              <input value={header.deliveryService ?? ''} readOnly className="srd-ro" />

              <label>ReceivedBy</label>
              {headerReadOnly
                ? <input value={header.receivedBy ?? ''} readOnly className="srd-ro" />
                : (
                  <select value={receivedByOid} onChange={(e) => markHeader(setReceivedByOid)(e.target.value)}>
                    <option value="">— none —</option>
                    {(lookups?.employees ?? []).map((o) => <option key={o.id} value={o.id}>{o.name ?? '—'}</option>)}
                  </select>
                )}
              <label>DateReceived</label>
              {headerReadOnly
                ? <input value={fmtLocalDate(header.dateReceived)} readOnly className="srd-ro" />
                : <UsDateInput className="srd-date" value={dateReceived} onChange={markHeader(setDateReceived)} />}

              <label>DateShipped</label>
              {headerReadOnly
                ? <input value={fmtLocalDate(header.dateShipped)} readOnly className="srd-ro" />
                : <UsDateInput className="srd-date" value={dateShipped} onChange={markHeader(setDateShipped)} />}
              <label>ContractLab</label>
              <input value={header.contractLab ?? ''} readOnly className="srd-ro" />

              <label>Tracking#</label>
              <input value={header.trackingNo ?? ''} readOnly className="srd-ro" />
              <label>DaysElapsed</label>
              <input type="number" min={0} value={daysElapsed} readOnly={headerReadOnly}
                     className={headerReadOnly ? 'srd-ro' : ''}
                     onChange={(e) => markHeader(setDaysElapsed)(e.target.value)} />

              <label>DueDate</label>
              <input value={fmtLocalDate(header.dueDate)} readOnly className="srd-ro" />
              <label>Status</label>
              <input value={header.statusText ?? ''} readOnly className="srd-ro" />

              <label>Remark</label>
              <textarea rows={2} maxLength={4000} value={remark} readOnly={headerReadOnly}
                        className={headerReadOnly ? 'srd-ro' : ''}
                        onChange={(e) => markHeader(setRemark)(e.target.value)} />
              <span /><span />
            </div>
          </section>
        )}
        {/* Mounted for the whole entry view (hidden off its tabs) so the tab counts are live. */}
        {isEntry && (
          <div className="srd-tabpane" hidden={tab !== 'attachments' && tab !== 'notes'}>
            <PreLoginDocsPanel cocOid={orderId} owner={ENTRY_DOCS} embedded readOnly={!canWrite}
                               activeTab={tab === 'notes' ? 'note' : 'attachment'} onCounts={setDocCounts} />
          </div>
        )}
      </div>

      {actionBar}
    </div>
  );
}
